// src/services/configService.ts
// Runtime configuration service for EasyMap3
// Loads config from /config.json in production, falls back to import.meta.env in development
//
// DOCUMENTATION: See docs/runtime-configuration.md for full usage guide
//
// Quick reference:
// - Use helper functions (getBackendUrl, getMapboxToken, etc.) instead of import.meta.env.VITE_*
// - Config is loaded in main.tsx BEFORE React mounts
// - For code that runs at module load time, use lazy initialization patterns

import type { RuntimeConfig } from '../types/config';

class ConfigService {
  private config: RuntimeConfig | null = null;
  private loadPromise: Promise<RuntimeConfig> | null = null;

  /**
   * Load configuration. Called once before React mounts.
   * Returns a promise that resolves when config is loaded.
   */
  async load(): Promise<RuntimeConfig> {
    // If already loaded, return immediately
    if (this.config) {
      return this.config;
    }

    // If loading in progress, return the same promise
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.loadPromise = this.fetchConfig();
    this.config = await this.loadPromise;

    // Validate required config at startup - fail fast
    this.validateRequiredConfig(this.config);

    // Also set on window for debugging and legacy access
    window.__RUNTIME_CONFIG__ = this.config;

    return this.config;
  }

  /**
   * Validate required configuration values at startup.
   * Throws if any required config is missing - fail fast principle.
   */
  private validateRequiredConfig(config: RuntimeConfig): void {
    const required: (keyof RuntimeConfig)[] = [
      'CROSS_APP_CMS_URL',
      'BACKEND_URL',
      'MAPBOX_ACCESS_TOKEN',
      'FUSIONAUTH_SERVER_URL',
      'FUSIONAUTH_CLIENT_ID',
    ];

    const missing = required.filter(key => !config[key]);

    if (missing.length > 0) {
      throw new Error(
        `Missing required configuration: ${missing.join(', ')}. ` +
        `Check your .env file or /config.json.`
      );
    }
  }

  private async fetchConfig(): Promise<RuntimeConfig> {
    try {
      // In development, fall back to import.meta.env for backward compatibility
      if (import.meta.env.DEV) {
        console.log('[ConfigService] Development mode - using import.meta.env');
        return this.getDevConfig();
      }

      console.log('[ConfigService] Production mode - fetching /config.json');
      const response = await fetch('/config.json', {
        cache: 'no-cache', // Always get fresh config
      });

      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status}`);
      }

      const config = await response.json();
      console.log('[ConfigService] Loaded runtime config successfully');
      return config;
    } catch (error) {
      console.error('[ConfigService] Failed to load runtime config, falling back to build-time values:', error);
      return this.getDevConfig();
    }
  }

  /**
   * Fallback config for development mode using import.meta.env
   */
  private getDevConfig(): RuntimeConfig {
    return {
      FUSIONAUTH_SERVER_URL: import.meta.env.VITE_FUSIONAUTH_SERVER_URL || '',
      FUSIONAUTH_CLIENT_ID: import.meta.env.VITE_FUSIONAUTH_CLIENT_ID || '',
      FUSIONAUTH_REDIRECT: import.meta.env.VITE_FUSIONAUTH_REDIRECT || '',
      FUSIONAUTH_LOGOUT_REDIRECT: import.meta.env.VITE_FUSIONAUTH_LOGOUT_REDIRECT || '',
      FUSIONAUTH_APPLICATION_ID: import.meta.env.VITE_FUSIONAUTH_APPLICATION_ID || '',
      BACKEND_URL: import.meta.env.VITE_BACKEND_URL || '',
      BACKEND_NAME: import.meta.env.VITE_BACKEND_NAME || 'localhost',
      BACKEND_PORT: import.meta.env.VITE_BACKEND_PORT || '3540',
      TITLE_PROD: import.meta.env.VITE_TITLE_PROD || 'Dev',
      DEV_MODE: import.meta.env.VITE_DEV_MODE === 'true',
      CROSS_APP_CMS_URL: import.meta.env.VITE_CROSS_APP_CMS_URL,
      MAPBOX_ACCESS_TOKEN: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '',
      MAP_CENTER_LAT: Number(import.meta.env.VITE_MAP_CENTER_LAT) || 60,
      MAP_CENTER_LON: Number(import.meta.env.VITE_MAP_CENTER_LON) || -111,
      MAP_INITIAL_ZOOM: Number(import.meta.env.VITE_MAP_INTIAL_ZOOM) || 5,
      DUTYLOG_SERVICE_URL: import.meta.env.VITE_DUTYLOG_SERVICE_URL || '',
      CORS_ORIGIN: import.meta.env.VITE_CORS_ORIGIN || '',
      HOT_LICENSE_KEY: import.meta.env.VITE_HOT_KEY || '',
      NOMAD_AGENCY_ID: import.meta.env.VITE_NOMAD_AGENCY_ID || '',
      WEATHER_CONDITION_NESTED: import.meta.env.VITE_WEATHER_CONDITION_NESTED !== 'false',
    };
  }

  /**
   * Get the loaded configuration. Throws if not loaded.
   */
  get(): RuntimeConfig {
    if (!this.config) {
      throw new Error('Config not loaded. Call configService.load() before accessing config.');
    }
    return this.config;
  }

  /**
   * Check if config is loaded
   */
  isLoaded(): boolean {
    return this.config !== null;
  }
}

// Singleton export
export const configService = new ConfigService();

// Convenience getters for common values
export const getConfig = () => configService.get();

export const getFusionAuthConfig = () => {
  const config = configService.get();
  return {
    clientId: config.FUSIONAUTH_CLIENT_ID,
    redirectUri: config.FUSIONAUTH_REDIRECT,
    postLogoutRedirectUri: config.FUSIONAUTH_LOGOUT_REDIRECT,
    serverUrl: config.FUSIONAUTH_SERVER_URL,
    applicationId: config.FUSIONAUTH_APPLICATION_ID,
  };
};

export const getBackendUrl = () => configService.get().BACKEND_URL;

export const getBackendBaseUrl = () => {
  const config = configService.get();
  const protocol = window.location.protocol;
  return protocol === 'https:'
    ? `${protocol}//${config.BACKEND_NAME}`
    : `${protocol}//${config.BACKEND_NAME}:${config.BACKEND_PORT}`;
};

export const getMapboxToken = () => configService.get().MAPBOX_ACCESS_TOKEN;

// Live Weather: true = legacy nested weather_condition shape (header.nesdis/header.name,
// old DP3 GeoServer), false = flat shape (nesdis/name, new internal GeoServer).
export const getWeatherConditionNested = () => configService.get().WEATHER_CONDITION_NESTED;

export const getMapDefaults = () => {
  const config = configService.get();
  return {
    center: [config.MAP_CENTER_LON, config.MAP_CENTER_LAT] as [number, number],
    zoom: config.MAP_INITIAL_ZOOM,
  };
};

export const getMapCenter = () => {
  const config = configService.get();
  return {
    lat: config.MAP_CENTER_LAT,
    lon: config.MAP_CENTER_LON,
  };
};

export const getMapInitialZoom = () => configService.get().MAP_INITIAL_ZOOM;

export const getCmsUrl = () => configService.get().CROSS_APP_CMS_URL as string;

/** Base URL for CMS API calls — routed through EM3 backend proxy which adds auth */
export const getCmsApiUrl = (): string => '/api/cms';

export const isDevMode = () => configService.get().DEV_MODE;

export const getEnvironmentTitle = () => configService.get().TITLE_PROD;

export const getDutyLogServiceUrl = () => configService.get().DUTYLOG_SERVICE_URL;

export const getHotLicenseKey = () => configService.get().HOT_LICENSE_KEY;

// Nomad Integration - routes through EM3 backend proxy for observability
export const getNomadApiUrl = () => {
  return `${getBackendBaseUrl()}/api/nomad`;
};
export const getNomadAgencyId = () => configService.get().NOMAD_AGENCY_ID || '';
export const getNomadConfig = () => {
  return {
    apiUrl: getNomadApiUrl(),
    agencyId: getNomadAgencyId(),
  };
};
